import React from 'react';
import { Alert, Badge, Button, Col, Form, Row, Tab, Table, Tabs } from 'react-bootstrap';
import { requests } from '../../../lib/helper/agent';
import * as API_ROUTES from '../../../lib/routing/api';
import FileUploader from '../../file/FileUploader';
import MASpinner from '../../common/MASpinner';
import { niceDateFormatter, niceDateTimeFormatter } from '../../../lib/helper/formatting';
import { downloadCashbookImportReport } from '../../../lib/helper/downloadHelper';

const STEPS = { UPLOAD: 'upload', PREVIEW: 'preview', RESULT: 'result' };

const MATCHED_BY_LABELS = {
  account_and_sort_code: 'Account number + sort code',
  account_number: 'Account number',
  consultant_code: 'Consultant code',
};

function extractApiError(err, fallback = 'An error occurred. Please try again.') {
  return err?.response?.body?.['hydra:description'] || err?.response?.body?.detail || fallback;
}

function pluralise(count, singular, plural) {
  return count === 1 ? singular : plural;
}

function getInitialState() {
  return {
    step: STEPS.UPLOAD,
    selectedFile: null,
    reconciliationDateOverride: '',
    isUploading: false,
    temporaryFileId: null,
    previewData: null,
    commitData: null,
    error: null,
    history: [],
    isLoadingHistory: false,
  };
}

/**
 * MED-886 - admin wizard for importing the external cashbook reconciliation spreadsheet.
 *
 * Mirrors the CCSD fee schedule import: upload to a temporary file, dry run to preview, then commit.
 * The reconciliation date is read from the file's "Reconciliation Date" cell and only sent back to the API
 * when the user deliberately overrides it.
 */
class CashbookImportTab extends React.Component {
  constructor(props) {
    super(props);
    this.state = getInitialState();
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchHistory();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  fetchHistory() {
    this.setState({ isLoadingHistory: true });

    requests
      .get(API_ROUTES.CONSULTANT_CASHBOOK_IMPORTS, {
        'order[createdAt]': 'DESC',
        itemsPerPage: 20,
      })
      .then((response) => {
        if (!this._isMounted) return;
        this.setState({
          history: response['hydra:member'] || [],
          isLoadingHistory: false,
        });
      })
      .catch(() => {
        if (!this._isMounted) return;
        this.setState({ isLoadingHistory: false });
      });
  }

  handleFileAdded(e) {
    const file = e.target.files[0] || null;
    this.setState({ selectedFile: file, error: null });
  }

  buildImportBody(dryRun) {
    const { temporaryFileId, reconciliationDateOverride } = this.state;
    const body = { temporaryFileId, dryRun };

    // Only ever sent when the user has deliberately changed the date read from the file.
    if (reconciliationDateOverride) {
      body.reconciliationDate = reconciliationDateOverride;
    }

    return body;
  }

  handlePreview() {
    const { selectedFile } = this.state;

    if (!selectedFile) {
      this.setState({ error: 'Please select a CSV or XLSX file to upload.' });
      return;
    }

    this.setState({ isUploading: true, error: null });

    requests
      .upload(API_ROUTES.TEMPORARY_CASHBOOK_IMPORT_FILES, selectedFile)
      .then((uploadResponse) => {
        if (!this._isMounted) return;
        const temporaryFileId = uploadResponse.id;
        this.setState({ temporaryFileId });

        return requests.post(API_ROUTES.CONSULTANT_CASHBOOK_IMPORTS, { temporaryFileId, dryRun: true });
      })
      .then((previewData) => {
        if (!this._isMounted || !previewData) return;
        this.setState({ isUploading: false, previewData, step: STEPS.PREVIEW });
      })
      .catch((err) => {
        if (!this._isMounted) return;
        this.setState({ isUploading: false, error: extractApiError(err, 'An error occurred while uploading the file. Please try again.') });
      });
  }

  handleRePreview() {
    this.setState({ isUploading: true, error: null });

    requests
      .post(API_ROUTES.CONSULTANT_CASHBOOK_IMPORTS, this.buildImportBody(true))
      .then((previewData) => {
        if (!this._isMounted || !previewData) return;
        this.setState({ isUploading: false, previewData });
      })
      .catch((err) => {
        if (!this._isMounted) return;
        this.setState({ isUploading: false, error: extractApiError(err, 'An error occurred while re-checking the file. Please try again.') });
      });
  }

  handleConfirm() {
    this.setState({ isUploading: true, error: null });

    requests
      .post(API_ROUTES.CONSULTANT_CASHBOOK_IMPORTS, this.buildImportBody(false))
      .then((commitData) => {
        if (!this._isMounted) return;
        this.setState({ isUploading: false, commitData, step: STEPS.RESULT });
        this.fetchHistory();
      })
      .catch((err) => {
        if (!this._isMounted) return;
        this.setState({ isUploading: false, error: extractApiError(err, 'An error occurred during import. Please try again.') });
      });
  }

  handleBack() {
    this.setState({ step: STEPS.UPLOAD, error: null, previewData: null, temporaryFileId: null, reconciliationDateOverride: '' });
  }

  handleReset() {
    this.setState(getInitialState(), () => this.fetchHistory());
  }

  renderUploadStep() {
    const { selectedFile, isUploading, error } = this.state;

    return (
      <>
        {error && <Alert variant="danger">{error}</Alert>}

        <Row className="mb-3">
          <Col sm={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Client Account Reconciliation Report</strong>
              </Form.Label>
              <FileUploader
                accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                multiple={false}
                onFilesAdded={this.handleFileAdded.bind(this)}
                requestInProgress={isUploading}
                title="Click here to select a CSV or XLSX file"
                sizeLimitMB={20}
              />
              {selectedFile && <small className="text-muted">Selected: {selectedFile.name}</small>}
            </Form.Group>
            <p className="hint-text">
              The reconciliation date is read from the &quot;Reconciliation Date&quot; cell in the spreadsheet. You will be able to review and, if necessary, override
              it before anything is saved.
            </p>
          </Col>
        </Row>

        <div className="mb-3">
          <Button variant="primary" onClick={this.handlePreview.bind(this)} disabled={isUploading || !selectedFile} className="mr-2">
            {isUploading ? 'Uploading...' : 'Preview Import'}
          </Button>
        </div>

        {this.renderHistory()}
      </>
    );
  }

  renderMatchedBy(matchedBy) {
    const entries = Object.entries(matchedBy || {}).filter(([, count]) => count > 0);

    if (entries.length === 0) {
      return null;
    }

    return (
      <p className="mb-0 mt-2">
        <small>Matched by: {entries.map(([key, count]) => `${MATCHED_BY_LABELS[key] || key} (${count})`).join(', ')}</small>
      </p>
    );
  }

  renderUnmatched(rows) {
    if (!rows || rows.length === 0) {
      return null;
    }

    return (
      <div className="mb-4">
        <Alert variant="warning" className="mb-2">
          <strong>
            {rows.length} {pluralise(rows.length, 'row was', 'rows were')} not applied.
          </strong>{' '}
          These rows in the spreadsheet could not be matched to a consultant, or their balance could not be read. They will be skipped if you continue.
        </Alert>
        <div style={{ maxHeight: '300px', overflow: 'auto' }}>
          <Table size="sm" bordered striped>
            <thead>
              <tr>
                <th>Row</th>
                <th>Consultant ID in file</th>
                <th>Name in file</th>
                <th className="text-right">Balance</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={`unmatched-${i}`}>
                  <td>{row.row}</td>
                  <td>{row.source_consultant_id || '—'}</td>
                  <td>{row.name || '—'}</td>
                  <td className="text-right">{row.balance || '—'}</td>
                  <td className="text-danger">{row.reason}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    );
  }

  renderMissingFromFile(rows) {
    if (!rows || rows.length === 0) {
      return null;
    }

    return (
      <div className="mb-4">
        <Alert variant="warning" className="mb-2">
          <strong>
            {rows.length} {pluralise(rows.length, 'consultant is', 'consultants are')} not in this file.
          </strong>{' '}
          Their existing balance and reconciliation date will be left exactly as they are, so anything shown below stays on the consultant dashboard at its
          previous date.
        </Alert>
        <div style={{ maxHeight: '300px', overflow: 'auto' }}>
          <Table size="sm" bordered striped>
            <thead>
              <tr>
                <th>Code</th>
                <th>Consultant</th>
                <th className="text-right">Existing balance</th>
                <th>Existing reconciliation date</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={`missing-${i}`}>
                  <td>{row.consultant_code || '—'}</td>
                  <td>{row.consultant_name || '—'}</td>
                  <td className="text-right">{row.existing_balance === null || typeof row.existing_balance === 'undefined' ? '—' : row.existing_balance}</td>
                  <td>{row.existing_reconciled_at ? niceDateFormatter(row.existing_reconciled_at) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    );
  }

  renderChanges(rows) {
    if (!rows || rows.length === 0) {
      return null;
    }

    return (
      <div className="mb-4">
        <h5>Balances to be applied ({rows.length})</h5>
        <div style={{ maxHeight: '400px', overflow: 'auto' }}>
          <Table size="sm" bordered striped hover>
            <thead>
              <tr>
                <th>Row</th>
                <th>Code</th>
                <th>Consultant</th>
                <th>Matched by</th>
                <th className="text-right">Previous</th>
                <th className="text-right">New</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={`change-${i}`}>
                  <td>{row.row}</td>
                  <td>{row.consultant_code || '—'}</td>
                  <td>{row.consultant_name || '—'}</td>
                  <td>{MATCHED_BY_LABELS[row.matched_by] || row.matched_by}</td>
                  <td className="text-right">{row.previous_balance === null || typeof row.previous_balance === 'undefined' ? '—' : row.previous_balance}</td>
                  <td className="text-right font-weight-bold">{row.new_balance}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    );
  }

  renderPreviewStep() {
    const { previewData, isUploading, error, reconciliationDateOverride } = this.state;
    const hasErrors = previewData?.errors?.length > 0;

    return (
      <>
        {error && <Alert variant="danger">{error}</Alert>}

        {hasErrors ? (
          <>
            <Alert variant="danger">
              <strong>
                Import cannot proceed — {previewData.errors.length} {pluralise(previewData.errors.length, 'error', 'errors')} found in the file.
              </strong>
              <br />
              Please fix the issues below and re-upload.
            </Alert>
            <div style={{ maxHeight: '300px', overflow: 'auto' }} className="mb-3">
              <Table size="sm" bordered>
                <thead>
                  <tr>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.errors.map((err, i) => (
                    <tr key={`error-${i}`}>
                      <td className="text-danger">{err}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        ) : (
          <>

            <Row className="mb-3">
              <Col sm={12} md={8}>
                <Form.Label>
                  <strong>Preview — nothing has been saved yet. Confirm to apply these balances.</strong>
                </Form.Label>
            <Alert variant="warning">
              Reconciliation date: <strong>
                {previewData?.reconciliationDate ? niceDateFormatter(previewData.reconciliationDate, 'DD MMMM YYYY') : '—'}
              </strong>{' '}
              {/* The raw cell value is an Excel serial rather than readable text, so only the parsed
                  date above is shown. The raw still appears in the error if it cannot be read. */}
              {previewData?.reconciliationDateSource === 'override' ? (
                <Badge variant="info">Overridden</Badge>
              ) : (
                <Badge variant="secondary">Read from file</Badge>
              )}
              <br />
              Rows processed: <strong>{previewData?.rowsProcessed ?? 0}</strong>
              <br />
              Consultants to update: <strong>{previewData?.consultantsUpdated ?? 0}</strong>
              <br />
              Rows skipped: <strong>{previewData?.rowsSkipped ?? 0}</strong>
              <br />
              Rows not matched to a consultant:{' '}
              <strong className={previewData?.rowsUnmatched > 0 ? 'text-danger' : ''}>{previewData?.rowsUnmatched ?? 0}</strong>
              <br />
              Consultants not in the file:{' '}
              <strong className={previewData?.consultantsMissingFromFile > 0 ? 'text-danger' : ''}>{previewData?.consultantsMissingFromFile ?? 0}</strong>
              {this.renderMatchedBy(previewData?.matchedBy)}
              {previewData?.reportFileName && (
                <Button variant="link" size="sm" className="p-0 mt-2" onClick={() => downloadCashbookImportReport(previewData.reportFileName)}>
                  <i className="fas fa-download mr-1" />
                  Download preview report
                </Button>
              )}
            </Alert>
              </Col>
              <Col sm={12} md={4}>
                <Form.Group className="mb-1">
                  <Form.Label>
                    <strong>Override the reconciliation date</strong>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={reconciliationDateOverride}
                    onChange={(e) => this.setState({ reconciliationDateOverride: e.target.value })}
                    disabled={isUploading}
                  />
                </Form.Group>
                <p className="hint-text">Only set this if the date read from the file is wrong. Re-check to see the preview for the date you have entered.</p>
                <Button variant="outline-primary" size="sm" onClick={this.handleRePreview.bind(this)} disabled={isUploading || !reconciliationDateOverride}>
                  {isUploading ? 'Re-checking...' : 'Re-check with this date'}
                </Button>
              </Col>
            </Row>

            <Form.Label>
              <strong>File Data</strong>
            </Form.Label>
            <Tabs variant="pills" className="p-1 bg-gray-200 m-0">
              <Tab eventKey="updates" title="Consultants with updated values">

                <Row>
                  <Col>
                    <div className="crud__section mb-3">
                      <Row>
                        <Col>
                {this.renderChanges(previewData?.changes)}

                          {!hasErrors && (
                              <Button variant="success" onClick={this.handleConfirm.bind(this)} disabled={isUploading}>
                                {isUploading ? 'Importing...' : 'Confirm & Import'}
                              </Button>
                          )}
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Tab>
              {previewData?.unmatched?.length > 0 && (
                  <Tab eventKey="unmatched" title="Unrecognised consultants">

                    <Row>
                      <Col>
                        <div className="crud__section mb-3">
                          <Row>
                            <Col>
                {this.renderUnmatched(previewData?.unmatched)}
                  </Col>
                </Row>
                </div>
                </Col>
                </Row>
              </Tab>
                  )}
              <Tab eventKey="notInFile" title="Consultants not in file">

                <Row>
                  <Col>
                    <div className="crud__section mb-3">
                      <Row>
                        <Col>
                {this.renderMissingFromFile(previewData?.missingFromFile)}

                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Tab>
            </Tabs>
          </>
        )}

        <div className="mt-3">
          <Button variant="secondary" className="mr-2" onClick={this.handleBack.bind(this)} disabled={isUploading}>
            Back
          </Button>
        </div>
      </>
    );
  }

  renderResultStep() {
    const { commitData } = this.state;

    return (
      <>
        <Alert variant="success">
          <strong>Cashbook balances imported successfully.</strong>
          <br />
          {commitData?.consultantsUpdated ?? 0} {pluralise(commitData?.consultantsUpdated ?? 0, 'consultant', 'consultants')} updated as at{' '}
          <strong>{commitData?.reconciliationDate ? niceDateFormatter(commitData.reconciliationDate, 'DD MMMM YYYY') : '—'}</strong>.
          {commitData?.rowsUnmatched > 0 && (
            <>
              <br />
              {commitData.rowsUnmatched} {pluralise(commitData.rowsUnmatched, 'row was', 'rows were')} not matched and{' '}
              {pluralise(commitData.rowsUnmatched, 'was', 'were')} skipped.
            </>
          )}
          {commitData?.consultantsMissingFromFile > 0 && (
            <>
              <br />
              {commitData.consultantsMissingFromFile} {pluralise(commitData.consultantsMissingFromFile, 'consultant was', 'consultants were')} not in the file
              and {pluralise(commitData.consultantsMissingFromFile, 'keeps its', 'keep their')} previous balance.
            </>
          )}
          {commitData?.reportFileName && (
            <>
              <br />
              <Button variant="link" size="sm" className="p-0 mt-2" onClick={() => downloadCashbookImportReport(commitData.reportFileName)}>
                <i className="fas fa-download mr-1" />
                Download import report
              </Button>
            </>
          )}
        </Alert>
        <Button variant="primary" onClick={this.handleReset.bind(this)}>
          Import Another
        </Button>
      </>
    );
  }

  renderHistory() {
    const { history, isLoadingHistory } = this.state;

    if (isLoadingHistory) {
      return <MASpinner msg="Loading import history..." />;
    }

    if (history.length === 0) {
      return (
        <Alert variant="secondary" className="mt-4">
          No cashbook imports have been made yet.
        </Alert>
      );
    }

    return (
      <div className="mt-4">
        <h5>Previous Imports</h5>
        <Table striped bordered hover size="sm">
          <thead>
            <tr>
              <th>Uploaded</th>
              <th>Reconciliation Date</th>
              <th>File</th>
              <th>Rows</th>
              <th>Updated</th>
              <th>Skipped</th>
              <th>Unmatched</th>
              <th>Not in File</th>
              <th>Report</th>
            </tr>
          </thead>
          <tbody>
            {history.map((record) => (
              <tr key={record.id}>
                <td>{niceDateTimeFormatter(record.createdAt)}</td>
                <td>{record.reconciliationDate ? niceDateFormatter(record.reconciliationDate) : '—'}</td>
                <td>{record.sourceFileName || '—'}</td>
                <td>{record.rowsProcessed ?? 0}</td>
                <td>
                  <Badge variant={record.consultantsUpdated > 0 ? 'success' : 'secondary'}>{record.consultantsUpdated ?? 0}</Badge>
                </td>
                <td>
                  <Badge variant="secondary">{record.rowsSkipped ?? 0}</Badge>
                </td>
                <td>
                  <Badge variant={record.rowsUnmatched > 0 ? 'warning' : 'secondary'}>{record.rowsUnmatched ?? 0}</Badge>
                </td>
                <td>
                  <Badge variant={record.consultantsMissingFromFile > 0 ? 'warning' : 'secondary'}>{record.consultantsMissingFromFile ?? 0}</Badge>
                </td>
                <td>
                  {record.reportFileName ? (
                    <Button variant="link" size="sm" className="p-0" onClick={() => downloadCashbookImportReport(record.reportFileName)}>
                      <i className="fas fa-download mr-1" />
                      Download
                    </Button>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  }

  render() {
    const { step } = this.state;

    return (
      <div>
        {step === STEPS.UPLOAD && this.renderUploadStep()}
        {step === STEPS.PREVIEW && this.renderPreviewStep()}
        {step === STEPS.RESULT && (
          <>
            {this.renderResultStep()}
            <div className="mt-4">{this.renderHistory()}</div>
          </>
        )}
      </div>
    );
  }
}

export default CashbookImportTab;
